import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  AutoAwesome,
  Close,
  Delete,
  Edit,
  Inventory2,
  Payments,
  Refresh,
  Storefront,
  TrendingUp,
  Wallet,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  createExpense,
  createExpenseWallet,
  createIncome,
  createProduct,
  deleteExpense,
  deleteExpenseWallet,
  deleteIncome,
  deleteProduct,
  fetchExpenseCurrencies,
  fetchExpensePreferences,
  fetchExpenseSummary,
  fetchExpenseWallets,
  fetchExpenses,
  fetchIncomeEntries,
  fetchIncomeSummary,
  fetchProducts,
  updateExpense,
  updateExpensePreferences,
  updateIncome,
  updateProduct,
} from '../hooks/hooks';

const PAGE_SIZE = 50;
const NEW_FEATURE_BANNER_KEY = 'zenopay_expenses_income_products_banner_dismissed';

const PERIODS = [
  { value: 'day', label: 'Day', statKey: 'day', statLabel: 'Today' },
  { value: 'week', label: 'Week', statKey: 'week', statLabel: 'This Week' },
  { value: 'month', label: 'Month', statKey: 'month', statLabel: 'This Month' },
  { value: 'year', label: 'Year', statKey: 'year', statLabel: 'This Year' },
];

const CATEGORIES = [
  { value: 'inventory', label: 'Inventory / Stock' },
  { value: 'rent', label: 'Rent' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'salaries', label: 'Salaries & Wages' },
  { value: 'transport', label: 'Transport' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'food', label: 'Food & Groceries' },
  { value: 'equipment', label: 'Equipment & Supplies' },
  { value: 'loan_repayment', label: 'Loan Repayment' },
  { value: 'taxes', label: 'Taxes & Fees' },
  { value: 'health', label: 'Health' },
  { value: 'education', label: 'Education' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'other', label: 'Other' },
];

const INCOME_CATEGORIES = [
  { value: 'sales', label: 'Product Sales' },
  { value: 'services', label: 'Services Rendered' },
  { value: 'investment', label: 'Investment' },
  { value: 'loan', label: 'Loan / Financing' },
  { value: 'other', label: 'Other' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatMoney = (val) => Number(val || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const emptyExpenseForm = (walletId, currency) => ({
  wallet: walletId || '',
  amount: '',
  currency: currency || 'TZS',
  category: 'other',
  description: '',
  spent_at: todayISO(),
});

const emptyIncomeForm = (walletId, currency) => ({
  wallet: walletId || '',
  product: '',
  quantity: '1',
  amount: '',
  currency: currency || 'TZS',
  category: 'other',
  description: '',
  received_at: todayISO(),
});

const emptyProductForm = (walletId, currency) => ({
  wallet: walletId || '',
  item_type: 'product',
  name: '',
  buy_price: '',
  sell_price: '',
  currency: currency || 'TZS',
  is_active: true,
});

const StatCard = ({ label, value, currency, active, cardBg, borderClr }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${active ? '#D32F2F' : borderClr}`,
      bgcolor: cardBg,
      borderRadius: '12px',
      height: '100%',
    }}
  >
    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Typography variant="body2" color="text.secondary" noWrap mb={0.75}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {formatMoney(value)} <Typography component="span" variant="caption" color="text.secondary">{currency}</Typography>
      </Typography>
    </CardContent>
  </Card>
);

const Expenses = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';
  const queryClient = useQueryClient();

  const [recordType, setRecordType] = useState('expenses');
  const [period, setPeriod] = useState('month');
  const [selectedWalletId, setSelectedWalletId] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);

  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm());
  const [expenseFormError, setExpenseFormError] = useState('');

  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeForm, setIncomeForm] = useState(emptyIncomeForm());
  const [incomeFormError, setIncomeFormError] = useState('');
  const [incomePage, setIncomePage] = useState(0);

  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(emptyProductForm());
  const [productFormError, setProductFormError] = useState('');

  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessCurrency, setBusinessCurrency] = useState('TZS');
  const [businessFormError, setBusinessFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteIncomeTarget, setDeleteIncomeTarget] = useState(null);
  const [deleteProductTarget, setDeleteProductTarget] = useState(null);
  const [deleteWalletTarget, setDeleteWalletTarget] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(NEW_FEATURE_BANNER_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismissBanner = () => {
    setBannerDismissed(true);
    try {
      localStorage.setItem(NEW_FEATURE_BANNER_KEY, '1');
    } catch {
      // ignore — worst case the banner reappears next visit
    }
  };

  const {
    data: wallets,
    error: walletsError,
    refetch: refetchWallets,
  } = useQuery({
    queryKey: ['expenseWallets'],
    queryFn: fetchExpenseWallets,
    refetchOnWindowFocus: false,
  });

  const { data: currencyOptions } = useQuery({
    queryKey: ['expenseCurrencies'],
    queryFn: fetchExpenseCurrencies,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { data: preferences } = useQuery({
    queryKey: ['expensePreferences'],
    queryFn: fetchExpensePreferences,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (currency === null && preferences?.default_currency) {
      setCurrency(preferences.default_currency);
    }
  }, [preferences, currency]);

  const updatePreferencesMutation = useMutation({
    mutationFn: updateExpensePreferences,
    onSuccess: (data) => queryClient.setQueryData(['expensePreferences'], data),
  });

  const handleCurrencyChange = (value) => {
    setCurrency(value);
    updatePreferencesMutation.mutate({ default_currency: value });
  };

  const walletList = Array.isArray(wallets) ? wallets : [];
  const personalWallet = walletList.find((w) => w.wallet_type === 'personal');
  const businessWallets = walletList.filter((w) => w.wallet_type === 'business');
  const currencies = Array.isArray(currencyOptions) ? currencyOptions : [];

  const summaryParams = useMemo(() => ({
    period,
    ...(selectedWalletId !== 'all' ? { wallet: selectedWalletId } : {}),
    ...(currency ? { currency } : {}),
  }), [period, selectedWalletId, currency]);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['expenseSummary', summaryParams],
    queryFn: () => fetchExpenseSummary(summaryParams),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const expenseParams = useMemo(() => ({
    page: page + 1,
    period,
    ...(selectedWalletId !== 'all' ? { wallet: selectedWalletId } : {}),
    ...(categoryFilter ? { category: categoryFilter } : {}),
    ...(currency ? { currency } : {}),
  }), [page, period, selectedWalletId, categoryFilter, currency]);

  const {
    data: expensesData,
    isLoading: expensesLoading,
    error: expensesError,
    refetch: refetchExpenses,
  } = useQuery({
    queryKey: ['expenses', expenseParams],
    queryFn: () => fetchExpenses(expenseParams),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(expensesData?.results) ? expensesData.results : [];
  const count = expensesData?.count || 0;

  const { data: products, refetch: refetchProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProducts(),
    refetchOnWindowFocus: false,
  });
  const productList = Array.isArray(products) ? products : [];

  const incomeSummaryParams = useMemo(() => ({
    period,
    ...(selectedWalletId !== 'all' ? { wallet: selectedWalletId } : {}),
    ...(currency ? { currency } : {}),
  }), [period, selectedWalletId, currency]);

  const {
    data: incomeSummary,
    isLoading: incomeSummaryLoading,
    error: incomeSummaryError,
    refetch: refetchIncomeSummary,
  } = useQuery({
    queryKey: ['incomeSummary', incomeSummaryParams],
    queryFn: () => fetchIncomeSummary(incomeSummaryParams),
    enabled: recordType === 'income',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const incomeParams = useMemo(() => ({
    page: incomePage + 1,
    period,
    ...(selectedWalletId !== 'all' ? { wallet: selectedWalletId } : {}),
    ...(currency ? { currency } : {}),
  }), [incomePage, period, selectedWalletId, currency]);

  const {
    data: incomeData,
    isLoading: incomeLoading,
    error: incomeError,
    refetch: refetchIncome,
  } = useQuery({
    queryKey: ['incomeEntries', incomeParams],
    queryFn: () => fetchIncomeEntries(incomeParams),
    enabled: recordType === 'income',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const incomeRows = Array.isArray(incomeData?.results) ? incomeData.results : [];
  const incomeCount = incomeData?.count || 0;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['expenseWallets'] });
    queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
  };

  const invalidateIncome = () => {
    queryClient.invalidateQueries({ queryKey: ['incomeSummary'] });
    queryClient.invalidateQueries({ queryKey: ['incomeEntries'] });
  };

  const invalidateProducts = () => queryClient.invalidateQueries({ queryKey: ['products'] });

  const createExpenseMutation = useMutation({
    mutationFn: createExpense,
    onSuccess: () => {
      invalidateAll();
      setExpenseDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Expense recorded.' });
    },
    onError: (err) => setExpenseFormError(err?.response?.data?.detail || 'Failed to save expense.'),
  });

  const updateExpenseMutation = useMutation({
    mutationFn: updateExpense,
    onSuccess: () => {
      invalidateAll();
      setExpenseDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Expense updated.' });
    },
    onError: (err) => setExpenseFormError(err?.response?.data?.detail || 'Failed to update expense.'),
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => {
      invalidateAll();
      setDeleteTarget(null);
      setSnackbar({ severity: 'success', message: 'Expense deleted.' });
    },
    onError: () => setSnackbar({ severity: 'error', message: 'Failed to delete expense.' }),
  });

  const createWalletMutation = useMutation({
    mutationFn: createExpenseWallet,
    onSuccess: (wallet) => {
      queryClient.invalidateQueries({ queryKey: ['expenseWallets'] });
      setBusinessDialogOpen(false);
      setBusinessName('');
      setBusinessCurrency(currency || 'TZS');
      setSelectedWalletId(wallet.id);
      setSnackbar({ severity: 'success', message: 'Business added.' });
    },
    onError: (err) => setBusinessFormError(err?.response?.data?.name?.[0] || 'Failed to add business.'),
  });

  const deleteWalletMutation = useMutation({
    mutationFn: deleteExpenseWallet,
    onSuccess: () => {
      invalidateAll();
      setSelectedWalletId('all');
      setDeleteWalletTarget(null);
      setSnackbar({ severity: 'success', message: 'Business removed.' });
    },
    onError: () => setSnackbar({ severity: 'error', message: 'Failed to remove business.' }),
  });

  const createIncomeMutation = useMutation({
    mutationFn: createIncome,
    onSuccess: () => {
      invalidateIncome();
      setIncomeDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Income recorded.' });
    },
    onError: (err) => setIncomeFormError(err?.response?.data?.amount?.[0] || err?.response?.data?.detail || 'Failed to save income.'),
  });

  const updateIncomeMutation = useMutation({
    mutationFn: updateIncome,
    onSuccess: () => {
      invalidateIncome();
      setIncomeDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Income updated.' });
    },
    onError: (err) => setIncomeFormError(err?.response?.data?.amount?.[0] || err?.response?.data?.detail || 'Failed to update income.'),
  });

  const deleteIncomeMutation = useMutation({
    mutationFn: deleteIncome,
    onSuccess: () => {
      invalidateIncome();
      setDeleteIncomeTarget(null);
      setSnackbar({ severity: 'success', message: 'Income deleted.' });
    },
    onError: () => setSnackbar({ severity: 'error', message: 'Failed to delete income.' }),
  });

  const createProductMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      invalidateProducts();
      setProductDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Product added.' });
    },
    onError: (err) => setProductFormError(err?.response?.data?.name?.[0] || err?.response?.data?.sell_price?.[0] || 'Failed to save product.'),
  });

  const updateProductMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      invalidateProducts();
      setProductDialogOpen(false);
      setSnackbar({ severity: 'success', message: 'Product updated.' });
    },
    onError: (err) => setProductFormError(err?.response?.data?.name?.[0] || err?.response?.data?.sell_price?.[0] || 'Failed to update product.'),
  });

  const deleteProductMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      invalidateProducts();
      setDeleteProductTarget(null);
      setSnackbar({ severity: 'success', message: 'Product deleted.' });
    },
    onError: () => setSnackbar({ severity: 'error', message: 'Failed to delete product.' }),
  });

  const openAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm(emptyExpenseForm(personalWallet?.id, currency));
    setExpenseFormError('');
    setExpenseDialogOpen(true);
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      wallet: expense.wallet,
      amount: String(expense.amount),
      currency: expense.currency,
      category: expense.category,
      description: expense.description || '',
      spent_at: expense.spent_at,
    });
    setExpenseFormError('');
    setExpenseDialogOpen(true);
  };

  const handleExpenseSubmit = () => {
    if (!expenseForm.wallet) return setExpenseFormError('Choose a wallet.');
    const amount = Number(expenseForm.amount);
    if (!amount || amount <= 0) return setExpenseFormError('Enter a valid amount.');
    if (!expenseForm.currency) return setExpenseFormError('Choose a currency.');
    if (!expenseForm.spent_at) return setExpenseFormError('Choose a date.');

    const payload = {
      wallet: expenseForm.wallet,
      amount,
      currency: expenseForm.currency,
      category: expenseForm.category,
      description: expenseForm.description.trim(),
      spent_at: expenseForm.spent_at,
    };

    if (editingExpense) {
      updateExpenseMutation.mutate({ id: editingExpense.id, ...payload });
    } else {
      createExpenseMutation.mutate(payload);
    }
  };

  const handleBusinessSubmit = () => {
    const name = businessName.trim();
    if (!name) return setBusinessFormError('Business name is required.');
    setBusinessFormError('');
    createWalletMutation.mutate({ name, wallet_type: 'business', currency: businessCurrency });
  };

  const openAddIncome = () => {
    setEditingIncome(null);
    setIncomeForm(emptyIncomeForm(selectedWalletId !== 'all' ? selectedWalletId : personalWallet?.id, currency));
    setIncomeFormError('');
    setIncomeDialogOpen(true);
  };

  const openEditIncome = (income) => {
    setEditingIncome(income);
    setIncomeForm({
      wallet: income.wallet,
      product: income.product || '',
      quantity: String(income.quantity || '1'),
      amount: String(income.amount),
      currency: income.currency,
      category: income.category,
      description: income.description || '',
      received_at: income.received_at,
    });
    setIncomeFormError('');
    setIncomeDialogOpen(true);
  };

  const handleIncomeProductChange = (productId) => {
    const product = productList.find((p) => String(p.id) === String(productId));
    if (!product) {
      setIncomeForm((f) => ({ ...f, product: '' }));
      return;
    }
    const quantity = Number(incomeForm.quantity) || 1;
    setIncomeForm((f) => ({
      ...f,
      product: product.id,
      currency: product.currency,
      amount: String((quantity * Number(product.sell_price)).toFixed(2)),
      category: product.item_type === 'service' ? 'services' : 'sales',
    }));
  };

  const handleIncomeQuantityChange = (quantity) => {
    const product = productList.find((p) => String(p.id) === String(incomeForm.product));
    setIncomeForm((f) => ({
      ...f,
      quantity,
      amount: product ? String(((Number(quantity) || 0) * Number(product.sell_price)).toFixed(2)) : f.amount,
    }));
  };

  const handleIncomeSubmit = () => {
    if (!incomeForm.wallet) return setIncomeFormError('Choose a wallet.');
    const amount = incomeForm.amount === '' ? null : Number(incomeForm.amount);
    if (!incomeForm.product && (!amount || amount <= 0)) return setIncomeFormError('Enter a valid amount.');
    if (!incomeForm.currency) return setIncomeFormError('Choose a currency.');
    if (!incomeForm.received_at) return setIncomeFormError('Choose a date.');

    const payload = {
      wallet: incomeForm.wallet,
      product: incomeForm.product || null,
      quantity: Number(incomeForm.quantity) || 1,
      currency: incomeForm.currency,
      category: incomeForm.category,
      description: incomeForm.description.trim(),
      received_at: incomeForm.received_at,
    };
    if (amount) payload.amount = amount;

    if (editingIncome) {
      updateIncomeMutation.mutate({ id: editingIncome.id, ...payload });
    } else {
      createIncomeMutation.mutate(payload);
    }
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm(emptyProductForm(selectedWalletId !== 'all' ? selectedWalletId : businessWallets[0]?.id, currency));
    setProductFormError('');
    setProductDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      wallet: product.wallet,
      item_type: product.item_type,
      name: product.name,
      buy_price: String(product.buy_price),
      sell_price: String(product.sell_price),
      currency: product.currency,
      is_active: product.is_active,
    });
    setProductFormError('');
    setProductDialogOpen(true);
  };

  const handleProductSubmit = () => {
    if (!productForm.wallet) return setProductFormError('Choose a wallet.');
    const name = productForm.name.trim();
    if (!name) return setProductFormError('Name is required.');
    const sellPrice = Number(productForm.sell_price);
    if (!sellPrice || sellPrice <= 0) return setProductFormError('Enter a valid selling price.');
    const buyPrice = Number(productForm.buy_price) || 0;
    if (buyPrice < 0) return setProductFormError('Buy price cannot be negative.');

    const payload = {
      wallet: productForm.wallet,
      item_type: productForm.item_type,
      name,
      buy_price: buyPrice,
      sell_price: sellPrice,
      currency: productForm.currency,
      is_active: productForm.is_active,
    };

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, ...payload });
    } else {
      createProductMutation.mutate(payload);
    }
  };

  const seriesLabel = (label) => {
    try {
      return format(new Date(label), period === 'year' ? 'MMM yyyy' : 'MMM d');
    } catch {
      return label;
    }
  };

  const displayCurrency = summary?.currency || currency || 'TZS';
  const maxSeries = Math.max(1, ...(summary?.series || []).map((s) => Number(s.total) || 0));
  const maxCategory = Math.max(1, ...(summary?.by_category || []).map((c) => Number(c.total) || 0));

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {!bannerDismissed && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.5,
            p: 2,
            borderRadius: '14px',
            color: '#fff',
            background: 'linear-gradient(120deg, #0c1a14 0%, #0d5c40 65%, #0BA480 100%)',
            boxShadow: '0 8px 26px rgba(11,164,128,0.25)',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{
            width: 40, height: 40, flexShrink: 0, borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AutoAwesome sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>
              ZenoPay is now getting better!
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
              Record your business expenses, sales, and products — and know your profits well.
            </Typography>
          </Box>
          <IconButton size="small" onClick={dismissBanner} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1.5}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <Payments sx={{ color: '#D32F2F', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>Expenses</Typography>
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Track personal and business spending, and see totals by day, week, month or year.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => { refetchWallets(); refetchSummary(); refetchExpenses(); refetchIncome(); refetchIncomeSummary(); refetchProducts(); }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          {recordType === 'expenses' && (
            <Button variant="contained" startIcon={<Add />} onClick={openAddExpense} sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' } }}>
              Add Expense
            </Button>
          )}
          {recordType === 'income' && (
            <Button variant="contained" startIcon={<Add />} onClick={openAddIncome} sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#08916F' } }}>
              Add Income
            </Button>
          )}
          {recordType === 'products' && (
            <Button variant="contained" startIcon={<Add />} onClick={openAddProduct} sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#08916F' } }}>
              Add Product
            </Button>
          )}
        </Stack>
      </Stack>

      {(walletsError || summaryError || expensesError || incomeSummaryError || incomeError) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Failed to load some expense data. Please try refreshing.
        </Alert>
      )}

      <ToggleButtonGroup
        value={recordType}
        exclusive
        size="small"
        onChange={(_, v) => { if (v) { setRecordType(v); setPage(0); setIncomePage(0); } }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="expenses" sx={{ textTransform: 'none', px: 2 }}>
          <Payments sx={{ fontSize: 16, mr: 0.75 }} /> Expenses
        </ToggleButton>
        <ToggleButton value="income" sx={{ textTransform: 'none', px: 2 }}>
          <TrendingUp sx={{ fontSize: 16, mr: 0.75 }} /> Income
        </ToggleButton>
        <ToggleButton value="products" sx={{ textTransform: 'none', px: 2 }}>
          <Inventory2 sx={{ fontSize: 16, mr: 0.75 }} /> Products & Services
        </ToggleButton>
      </ToggleButtonGroup>

      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Tabs
            value={selectedWalletId}
            onChange={(_, v) => { setSelectedWalletId(v); setPage(0); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            <Tab value="all" label="All" icon={<Wallet sx={{ fontSize: 18 }} />} iconPosition="start" />
            {personalWallet && (
              <Tab value={personalWallet.id} label="Personal" icon={<Wallet sx={{ fontSize: 18 }} />} iconPosition="start" />
            )}
            {businessWallets.map((w) => (
              <Tab
                key={w.id}
                value={w.id}
                iconPosition="start"
                icon={<Storefront sx={{ fontSize: 18 }} />}
                label={(
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    {w.name}
                    <Box
                      component="span"
                      role="button"
                      onClick={(e) => { e.stopPropagation(); setDeleteWalletTarget(w); }}
                      sx={{ display: 'inline-flex', ml: 0.5, opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </Box>
                  </Stack>
                )}
              />
            ))}
          </Tabs>
          <Button size="small" startIcon={<Add />} onClick={() => { setBusinessDialogOpen(true); setBusinessFormError(''); setBusinessCurrency(currency || 'TZS'); }} sx={{ mr: 1.5 }}>
            Add Business
          </Button>
        </Stack>
      </Card>

      <Stack direction="row" gap={1.5} mb={2} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          value={period}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) { setPeriod(v); setPage(0); } }}
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value} sx={{ textTransform: 'none', px: 2 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="Currency"
          value={currency || ''}
          onChange={(e) => { handleCurrencyChange(e.target.value); setPage(0); }}
          sx={{ minWidth: 190 }}
        >
          {currencies.map((c) => (
            <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          Sets your default currency for new expenses
        </Typography>
      </Stack>

      {recordType === 'expenses' && (
        <>
          {summaryLoading && !summary ? (
            <LinearProgress sx={{ mb: 3 }} />
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {PERIODS.map((p) => (
                <Grid item xs={6} sm={3} key={p.value}>
                  <StatCard
                    label={p.statLabel}
                    value={summary?.totals?.[p.statKey]}
                    currency={displayCurrency}
                    active={period === p.value}
                    cardBg={cardBg}
                    borderClr={borderClr}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Spending trend ({PERIODS.find((p) => p.value === period)?.label})
                  </Typography>
                  {(summary?.series || []).length === 0 ? (
                    <Typography color="text.secondary" fontSize={13} sx={{ py: 4 }} textAlign="center">
                      No expenses recorded for this period.
                    </Typography>
                  ) : (
                    <Stack gap={1}>
                      {summary.series.map((s) => (
                        <Stack key={s.label} direction="row" alignItems="center" gap={1.5}>
                          <Typography fontSize={12} color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                            {seriesLabel(s.label)}
                          </Typography>
                          <Box sx={{ flex: 1, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9', borderRadius: 1, overflow: 'hidden', height: 10 }}>
                            <Box sx={{ width: `${(Number(s.total) / maxSeries) * 100}%`, bgcolor: '#D32F2F', height: '100%' }} />
                          </Box>
                          <Typography fontSize={12.5} fontWeight={600} sx={{ width: 90, textAlign: 'right', flexShrink: 0 }}>
                            {formatMoney(s.total)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    By category
                  </Typography>
                  {(summary?.by_category || []).length === 0 ? (
                    <Typography color="text.secondary" fontSize={13} sx={{ py: 4 }} textAlign="center">
                      Nothing to break down yet.
                    </Typography>
                  ) : (
                    <Stack gap={1.25}>
                      {summary.by_category.map((c) => (
                        <Box key={c.category}>
                          <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography fontSize={12.5}>{c.category_display}</Typography>
                            <Typography fontSize={12.5} fontWeight={600}>{formatMoney(c.total)}</Typography>
                          </Stack>
                          <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9', borderRadius: 1, overflow: 'hidden', height: 6 }}>
                            <Box sx={{ width: `${(Number(c.total) / maxCategory) * 100}%`, bgcolor: '#0BA480', height: '100%' }} />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Expenses {expensesLoading ? '' : `(${count})`}
                </Typography>
                <TextField
                  size="small"
                  select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Wallet</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expensesLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">Loading expenses...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No expenses found for this filter.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' } }}>
                        <TableCell>
                          <Typography fontSize={12.5}>{format(new Date(r.spent_at), 'MMM d, yyyy')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.wallet_name}
                            icon={r.wallet_type === 'personal' ? <Wallet sx={{ fontSize: 14 }} /> : <Storefront sx={{ fontSize: 14 }} />}
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>{r.category_display}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} color="text.secondary">{r.description || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontSize={13} fontWeight={600}>{formatMoney(r.amount)} {r.currency}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditExpense(r)}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteTarget(r)}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {count > 0 && (
              <TablePagination
                component="div"
                count={count}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            )}
          </Card>
        </>
      )}

      {recordType === 'income' && (
        <>
          {incomeSummaryLoading && !incomeSummary ? (
            <LinearProgress sx={{ mb: 3 }} />
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {PERIODS.map((p) => (
                <Grid item xs={6} sm={3} key={p.value}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${period === p.value ? '#0BA480' : borderClr}`,
                      bgcolor: cardBg,
                      borderRadius: '12px',
                      height: '100%',
                    }}
                  >
                    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                      <Typography variant="body2" color="text.secondary" noWrap mb={0.75}>
                        {p.statLabel}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatMoney(incomeSummary?.totals?.[p.statKey]?.amount)}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">{displayCurrency}</Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#0BA480', fontWeight: 600 }}>
                        +{formatMoney(incomeSummary?.totals?.[p.statKey]?.profit)} profit
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Income trend ({PERIODS.find((p) => p.value === period)?.label})
                  </Typography>
                  {(incomeSummary?.series || []).length === 0 ? (
                    <Typography color="text.secondary" fontSize={13} sx={{ py: 4 }} textAlign="center">
                      No income recorded for this period.
                    </Typography>
                  ) : (
                    <Stack gap={1}>
                      {incomeSummary.series.map((s) => {
                        const maxIncomeSeries = Math.max(1, ...incomeSummary.series.map((row) => Number(row.amount) || 0));
                        return (
                          <Stack key={s.label} direction="row" alignItems="center" gap={1.5}>
                            <Typography fontSize={12} color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                              {seriesLabel(s.label)}
                            </Typography>
                            <Box sx={{ flex: 1, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9', borderRadius: 1, overflow: 'hidden', height: 10 }}>
                              <Box sx={{ width: `${(Number(s.amount) / maxIncomeSeries) * 100}%`, bgcolor: '#0BA480', height: '100%' }} />
                            </Box>
                            <Typography fontSize={12.5} fontWeight={600} sx={{ width: 90, textAlign: 'right', flexShrink: 0 }}>
                              {formatMoney(s.amount)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Best sellers
                  </Typography>
                  {(incomeSummary?.by_product || []).length === 0 ? (
                    <Typography color="text.secondary" fontSize={13} sx={{ py: 4 }} textAlign="center">
                      No product sales linked yet.
                    </Typography>
                  ) : (
                    <Stack gap={1.25}>
                      {incomeSummary.by_product.map((row) => (
                        <Box key={row.product_id}>
                          <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography fontSize={12.5}>{row.product_name} × {formatMoney(row.quantity)}</Typography>
                            <Typography fontSize={12.5} fontWeight={600} sx={{ color: '#0BA480' }}>+{formatMoney(row.profit)}</Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                Income {incomeLoading ? '' : `(${incomeCount})`}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Wallet</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Product / Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Profit</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomeLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">Loading income...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : incomeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No income found for this filter.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    incomeRows.map((r) => (
                      <TableRow key={r.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' } }}>
                        <TableCell>
                          <Typography fontSize={12.5}>{format(new Date(r.received_at), 'MMM d, yyyy')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.wallet_name}
                            icon={r.wallet_type === 'personal' ? <Wallet sx={{ fontSize: 14 }} /> : <Storefront sx={{ fontSize: 14 }} />}
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13}>
                            {r.product_name || r.description || r.category_display}
                            {r.product_name && r.quantity ? ` × ${formatMoney(r.quantity)}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontSize={13} fontWeight={600}>{formatMoney(r.amount)} {r.currency}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontSize={13} fontWeight={600} sx={{ color: '#0BA480' }}>+{formatMoney(r.profit)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditIncome(r)}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteIncomeTarget(r)}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {incomeCount > 0 && (
              <TablePagination
                component="div"
                count={incomeCount}
                page={incomePage}
                onPageChange={(_, p) => setIncomePage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            )}
          </Card>
        </>
      )}

      {recordType === 'products' && (
        <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
              Products & Services ({productList.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Wallet</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Buy Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Sell Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Margin</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No products or services yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productList.map((p) => (
                    <TableRow key={p.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' }, opacity: p.is_active ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={600}>{p.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={walletList.find((w) => w.id === p.wallet)?.name || '—'}
                          icon={<Storefront sx={{ fontSize: 14 }} />}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} color="text.secondary">{p.item_type === 'service' ? 'Service' : 'Product'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize={13}>{formatMoney(p.buy_price)} {p.currency}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize={13} fontWeight={600}>{formatMoney(p.sell_price)} {p.currency}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize={13} fontWeight={600} sx={{ color: Number(p.profit_per_unit) >= 0 ? '#0BA480' : '#D32F2F' }}>
                          {formatMoney(p.profit_per_unit)} ({Number(p.margin_percent).toFixed(0)}%)
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditProduct(p)}>
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteProductTarget(p)}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add / Edit expense dialog */}
      <Dialog open={expenseDialogOpen} onClose={() => setExpenseDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {expenseFormError && <Alert severity="error">{expenseFormError}</Alert>}
            <TextField
              select
              label="Wallet"
              size="small"
              value={expenseForm.wallet}
              onChange={(e) => setExpenseForm({ ...expenseForm, wallet: e.target.value })}
            >
              {personalWallet && <MenuItem value={personalWallet.id}>Personal</MenuItem>}
              {businessWallets.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" gap={1.5}>
              <TextField
                label="Amount"
                size="small"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
              />
              <TextField
                select
                label="Currency"
                size="small"
                sx={{ minWidth: 110 }}
                value={expenseForm.currency}
                onChange={(e) => setExpenseForm({ ...expenseForm, currency: e.target.value })}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Category"
              size="small"
              value={expenseForm.category}
              onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              size="small"
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              placeholder="e.g. Supplier restock"
            />
            <TextField
              label="Date"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={expenseForm.spent_at}
              onChange={(e) => setExpenseForm({ ...expenseForm, spent_at: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExpenseSubmit}
            disabled={createExpenseMutation.isPending || updateExpenseMutation.isPending}
            sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' } }}
          >
            {editingExpense ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add business dialog */}
      <Dialog open={businessDialogOpen} onClose={() => setBusinessDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Business</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {businessFormError && <Alert severity="error">{businessFormError}</Alert>}
            <TextField
              label="Business / Shop name"
              size="small"
              autoFocus
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Downtown Shop"
            />
            <TextField
              select
              label="Currency"
              size="small"
              value={businessCurrency}
              onChange={(e) => setBusinessCurrency(e.target.value)}
            >
              {currencies.map((c) => (
                <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBusinessDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBusinessSubmit}
            disabled={createWalletMutation.isPending}
            sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' } }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete expense confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            Delete this expense of {deleteTarget && formatMoney(deleteTarget.amount)} {deleteTarget?.currency}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteExpenseMutation.mutate(deleteTarget.id)}
            disabled={deleteExpenseMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete business confirm */}
      <Dialog open={!!deleteWalletTarget} onClose={() => setDeleteWalletTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove Business</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            Remove "{deleteWalletTarget?.name}" and all of its recorded expenses? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteWalletTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteWalletMutation.mutate(deleteWalletTarget.id)}
            disabled={deleteWalletMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit income dialog */}
      <Dialog open={incomeDialogOpen} onClose={() => setIncomeDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingIncome ? 'Edit Income' : 'Add Income'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {incomeFormError && <Alert severity="error">{incomeFormError}</Alert>}
            <TextField
              select
              label="Wallet"
              size="small"
              value={incomeForm.wallet}
              onChange={(e) => setIncomeForm({ ...incomeForm, wallet: e.target.value })}
            >
              {personalWallet && <MenuItem value={personalWallet.id}>Personal</MenuItem>}
              {businessWallets.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Product / Service (optional)"
              size="small"
              value={incomeForm.product}
              onChange={(e) => handleIncomeProductChange(e.target.value)}
            >
              <MenuItem value="">— Freeform income —</MenuItem>
              {productList.filter((p) => String(p.wallet) === String(incomeForm.wallet)).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name} ({formatMoney(p.sell_price)} {p.currency})</MenuItem>
              ))}
            </TextField>
            {incomeForm.product && (
              <TextField
                label="Quantity"
                size="small"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={incomeForm.quantity}
                onChange={(e) => handleIncomeQuantityChange(e.target.value)}
              />
            )}
            <Stack direction="row" gap={1.5}>
              <TextField
                label="Amount"
                size="small"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                value={incomeForm.amount}
                onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                helperText={incomeForm.product ? 'Auto-filled from selling price, editable' : ''}
              />
              <TextField
                select
                label="Currency"
                size="small"
                sx={{ minWidth: 110 }}
                value={incomeForm.currency}
                onChange={(e) => setIncomeForm({ ...incomeForm, currency: e.target.value })}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Category"
              size="small"
              value={incomeForm.category}
              onChange={(e) => setIncomeForm({ ...incomeForm, category: e.target.value })}
            >
              {INCOME_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              size="small"
              value={incomeForm.description}
              onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
              placeholder="e.g. Walk-in customer sale"
            />
            <TextField
              label="Date"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={incomeForm.received_at}
              onChange={(e) => setIncomeForm({ ...incomeForm, received_at: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleIncomeSubmit}
            disabled={createIncomeMutation.isPending || updateIncomeMutation.isPending}
            sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#08916F' } }}
          >
            {editingIncome ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit product dialog */}
      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingProduct ? 'Edit Product / Service' : 'Add Product / Service'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {productFormError && <Alert severity="error">{productFormError}</Alert>}
            <TextField
              select
              label="Wallet"
              size="small"
              value={productForm.wallet}
              onChange={(e) => setProductForm({ ...productForm, wallet: e.target.value })}
            >
              {personalWallet && <MenuItem value={personalWallet.id}>Personal</MenuItem>}
              {businessWallets.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              value={productForm.item_type}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setProductForm({ ...productForm, item_type: v }); }}
            >
              <ToggleButton value="product" sx={{ textTransform: 'none', flex: 1 }}>Product</ToggleButton>
              <ToggleButton value="service" sx={{ textTransform: 'none', flex: 1 }}>Service</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              label="Name"
              size="small"
              autoFocus
              value={productForm.name}
              onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
              placeholder="e.g. 1kg Rice"
            />
            <Stack direction="row" gap={1.5}>
              <TextField
                label="Buy Price (cost)"
                size="small"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                value={productForm.buy_price}
                onChange={(e) => setProductForm({ ...productForm, buy_price: e.target.value })}
              />
              <TextField
                label="Sell Price"
                size="small"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                value={productForm.sell_price}
                onChange={(e) => setProductForm({ ...productForm, sell_price: e.target.value })}
              />
            </Stack>
            <TextField
              select
              label="Currency"
              size="small"
              value={productForm.currency}
              onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
            >
              {currencies.map((c) => (
                <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
            {editingProduct && (
              <TextField
                select
                label="Status"
                size="small"
                value={productForm.is_active ? '1' : '0'}
                onChange={(e) => setProductForm({ ...productForm, is_active: e.target.value === '1' })}
              >
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Archived</MenuItem>
              </TextField>
            )}
            {Number(productForm.sell_price) > 0 && (
              <Typography variant="caption" color="text.secondary">
                Profit per unit: {formatMoney(Number(productForm.sell_price) - (Number(productForm.buy_price) || 0))} {productForm.currency}
                {' '}({(((Number(productForm.sell_price) - (Number(productForm.buy_price) || 0)) / Number(productForm.sell_price)) * 100).toFixed(0)}% margin)
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleProductSubmit}
            disabled={createProductMutation.isPending || updateProductMutation.isPending}
            sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#08916F' } }}
          >
            {editingProduct ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete income confirm */}
      <Dialog open={!!deleteIncomeTarget} onClose={() => setDeleteIncomeTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Income</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            Delete this income of {deleteIncomeTarget && formatMoney(deleteIncomeTarget.amount)} {deleteIncomeTarget?.currency}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIncomeTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteIncomeMutation.mutate(deleteIncomeTarget.id)}
            disabled={deleteIncomeMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete product confirm */}
      <Dialog open={!!deleteProductTarget} onClose={() => setDeleteProductTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            Delete "{deleteProductTarget?.name}"? Past income entries linked to it will keep their recorded amounts. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProductTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteProductMutation.mutate(deleteProductTarget.id)}
            disabled={deleteProductMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar && <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert>}
      </Snackbar>
    </Container>
  );
};

export default Expenses;
